'use client';

import React, { useCallback, useRef } from 'react';
import { tv } from 'tailwind-variants';
import { CustomForm } from '.generated/Feature.Irwinmitchell.model';
import GuidanceSection from './GuidanceSection';
import { RemixIcon } from 'helpers/RemixIcon';
import { SvgIcon } from 'helpers/SvgIcon';
import { useScrollElementIntoView } from 'lib/hooks/useScrollElementIntoView';
import {
  useAccordionItemContext,
  AccordionContextProvider,
} from 'helpers/Context/AccordionContext';
import { getTestProps } from 'lib/testing/utils';
import { ComponentRendering } from '@sitecore-content-sdk/nextjs';
import ModalWrapper from 'helpers/GenericWrappers/ModalWrapper/ModalWrapper';
import { Field } from '@sitecore-content-sdk/nextjs';
import { Content } from '.generated/Content/ConveyancingCalculator.model';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
import useIsEditing from 'lib/hooks/useIsEditing';

type AdditionalFeesBlock = {
  title?: Field<string>;
  description?: Field<string>;
  additionalFeesList: Content.ConveyancingCalculator.GenericFeeSlab_Item[];
};

export type ConveyancingStep2Props = {
  step2FeesExclVat?: FeeSection;
  step2MandatoryFees?: FeeSection;
  totalObj?: {
    label: string;
    amount: number | string;
  };
  // Use the same shape as DynamicForm to stay compatible with Sitecore form data
  fields?: CustomForm.Form['fields'];
  tabTitle?: Field<string>;
  additionalFeesBlock?: AdditionalFeesBlock;
  // Optional heading/subheading (since Sitecore fields may not exist here)
  headingText?: string;
  subheadingText?: string;
  guidanceBlock?: {
    title?: Field<string>;
    description?: Field<string>;
  };
};

type FeeItem = {
  label: string;
  amount: number | string;
  isTotal?: boolean;
  feeTooltipTitle?: Field<string>;
  feeTooltip?: Field<string>;
};

type FeeSection = {
  title: string;
  items: FeeItem[];
};

// Format currency value
const formatCurrency = (value: number | string): string => {
  if (typeof value === 'string') {
    // Extract number from string, format it, then replace in original string
    const match = value.match(/-?\d*\.?\d+/);
    if (match) {
      const num = parseFloat(match[0]);
      // Use the formatting from below
      const formattedNum = new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
        minimumFractionDigits: 2,
      }).format(num);
      // Replace first occurrence of number in string with formatted number
      return value.replace(match[0], formattedNum);
    }
    return value;
  }
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
  }).format(value);
};

const STYLES = tv({
  slots: {
    container: ['w-full', 'flex', 'flex-col', 'gap-general-spacing-gutter'],
    form: ['lg:w-[51.17%]', 'order-2', 'lg:order-1'],
    formGrid: ['flex', 'flex-col', 'lg:flex-row', 'lg:gap-margin-loose', 'gap-spacing-spacing-24'],
    fieldsColumn: ['flex', 'flex-col', 'gap-general-spacing-gutter'],
    fieldGroup: ['flex', 'flex-col', 'gap-general-spacing-gutter'],
    formCard: ['flex', 'flex-col', 'gap-spacing-spacing-24'],
    section: ['flex', 'flex-col', 'gap-padding-tight'],
    sectionTitle: [
      'font-typography-font-family-body',
      'font-semibold',
      'leading-typography-line-height-body-xlarge',
      'text-component-card-item-title',
      'text-typography-font-size-body-xlarge',
    ],
    feeList: ['flex', 'flex-col'],
    feeItem: ['flex', 'flex-col', 'gap-spacing-spacing-12'],
    feeRow: [
      'border-b',
      'border-color-input-border',
      'flex',
      'items-center',
      'justify-between',
      'overflow-clip',
      'px-0',
      'py-spacing-spacing-12',
    ],
    feeRowTotal: [
      'flex',
      'items-center',
      'justify-between',
      'overflow-clip',
      'px-0',
      'py-spacing-spacing-12',
    ],
    feeLabelContainer: ['flex', 'gap-spacing-spacing-8', 'items-center'],
    feeLabel: [
      'font-typography-font-family-body',
      'font-typography-font-weight-body',
      'leading-typography-line-height-body-medium',
      'text-color-core-color-1-Navy',
      'text-typography-font-size-body-medium',
      'underline',
      'decoration-solid',
      'cursor-pointer',
      'hover:no-underline',
    ],
    feeLabelTotal: [
      'font-typography-font-family-body',
      'font-typography-font-weight-body-bold',
      'leading-typography-line-height-body-medium',
      'text-color-core-color-1-Navy',
      'text-typography-font-size-body-medium',
    ],
    feeAmount: [
      'font-typography-font-family-body',
      'font-typography-font-weight-body',
      'leading-typography-line-height-body-medium',
      'text-color-core-color-1-Navy',
      'text-typography-font-size-body-medium',
      'text-right',
    ],
    feeAmountTotal: [
      'font-typography-font-family-body',
      'font-typography-font-weight-body-bold',
      'leading-typography-line-height-body-medium',
      'text-color-core-color-1-Navy',
      'text-typography-font-size-body-medium',
      'text-right',
    ],
    infoIcon: [
      'relative',
      'shrink-0',
      'size-4',
      'inline-block',
      'align-middle',
      'ml-spacing-spacing-4',
    ],
    totalSection: ['flex', 'flex-col'],
    totalRow: [
      'flex',
      'items-center',
      'justify-between',
      'overflow-clip',
      'px-0',
      'py-spacing-spacing-12',
    ],
    totalLabel: [
      'font-typography-font-family-body',
      'font-semibold',
      'leading-typography-line-height-body-xlarge',
      'text-color-core-color-1-Navy',
      'text-typography-font-size-body-xlarge',
    ],
    totalAmount: [
      'font-typography-font-family-body',
      'font-semibold',
      'leading-typography-line-height-body-xlarge',
      'text-color-core-color-1-Navy',
      'text-typography-font-size-body-xlarge',
      'text-right',
    ],
    ctaArea: [
      'bg-color-surface-white',
      'border-t',
      'border-component-profile-block-divider',
      'flex',
      'items-center',
      'justify-between',
      'px-margin-base',
      'py-0',
      'rounded-bl-general-border-radius-card',
      'rounded-br-general-border-radius-card',
      'shadow-[0px_0px_8px_0px_rgba(0,0,0,0.1)]',
    ],
    ctaLeft: ['flex', 'gap-spacing-spacing-16', 'h-24', 'items-center', 'px-0', 'py-padding-less'],
    ctaButtons: ['flex', 'gap-padding-micro', 'p-0'],
    ctaText: [
      'flex',
      'flex-col',
      'items-start',
      'justify-center',
      'leading-0',
      'text-color-core-color-1-Navy',
      'text-typography-font-size-body-medium',
    ],
    ctaTextBold: [
      'flex',
      'flex-col',
      'font-typography-font-family-body',
      'font-typography-font-weight-body-bold',
      'justify-center',
      'relative',
      'shrink-0',
    ],
    ctaTextRegular: [
      'flex',
      'flex-col',
      'font-typography-font-family-body',
      'font-typography-font-weight-body',
      'justify-center',
      'relative',
      'shrink-0',
      'whitespace-nowrap',
    ],
    ctaRight: ['flex', 'gap-padding-micro', 'items-center', 'justify-end', 'p-0'],
    printButton: ['flex', 'gap-spacing-spacing-8', 'items-center', 'p-spacing-spacing-2'],
  },
});

const ACCORDION_ITEM_STYLES = tv({
  defaultVariants: {
    isOpen: false,
    isVisible: true,
  },
  slots: {
    base: [
      'border',
      'border-component-accordion-item-border',
      'hover:border-component-accordion-item-border-hover',
      'rounded-component-accordion-item-border-radius',
      'focus-within:shadow-[0_0_0_3px_var(--color-core-color-4-SkyBlue)]',
      'break-inside-avoid', // Prevent breaking across columns
    ],
    wrapper: ['flex', 'flex-col', 'gap'],
    buttonWrapper: [
      'cursor-pointer',
      'duration-300',
      'flex',
      'items-center',
      'justify-between',
      'px-component-accordion-item-padding-x',
      'py-spacing-spacing-24',
      'transition-none',
      'w-full',
      'scroll-mt-[123px]',
      'hover:underline',
      'bg-component-accordion-item-surface-active',
      'rounded-tl-component-accordion-item-border-radius',
      'rounded-tr-component-accordion-item-border-radius',
      'focus:outline-none',
      'group',
      'text-left',
    ],
    contentContainer: [
      'duration-300',
      'ease-in-out',
      'grid',
      'overflow-hidden',
      'transition-all',
      'rounded-bl-component-accordion-item-border-radius',
      'rounded-br-component-accordion-item-border-radius',
      'bg-component-accordion-item-surface-active',
    ],
    contentContainerInner: ['overflow-hidden'],
    contentContainerInnerInner: [
      'px-component-accordion-item-padding-x',
      'pb-component-accordion-item-padding-y',
    ],
    iconWrapper: [
      'transform',
      'transition-transform',
      'mx-spacing-spacing-4',
      'text-component-accordion-item-icon',
      'group-hover:text-component-accordion-item-icon-hover',
      'group-focus:text-component-accordion-item-icon-hover',
    ],
    headingText: [
      'text-left',
      'font-typography-font-family-body',
      'font-typography-font-weight-body-bold',
      'leading-typography-line-height-body-large',
      'text-color-core-color-1-Navy',
      'text-typography-font-size-body-large',
    ],
    descriptionText: [
      'text-component-accordion-item-description',
      'text-left',
      'font-typography-font-family-body',
      'font-typography-font-weight-body',
      'leading-typography-line-height-body-medium',
      'text-color-core-color-1-Navy',
      'text-typography-font-size-body-medium',
    ],
  },
  variants: {
    isOpening: {
      false: {
        base: ['rounded-component-accordion-item-border-radius'],
        buttonWrapper: ['rounded-component-accordion-item-border-radius'],
        contentContainer: [
          'grid-rows-[0fr]',
          'opacity-0',
          'pointer-events-none',
          'select-none',
          'invisible',
        ],
        iconWrapper: [],
      },
      true: {
        base: [
          'rounded-component-accordion-item-border-radius',
          'border-component-accordion-item-border-active',
        ],
        buttonWrapper: [
          'rounded-t-component-accordion-item-border-radius',
          'pb-spacing-spacing-16',
          'underline',
          'decoration-solid',
        ],
        contentContainer: ['grid-rows-[1fr]', 'opacity-100', 'visible'],
        iconWrapper: ['rotate-180'],
      },
    },

    isVisible: {
      false: ['max-sm:hidden'],
      true: [],
    },
  },
});

const {
  container,
  form,
  formGrid,
  fieldsColumn,
  formCard,
  sectionTitle,
  feeList,
  feeItem,
  feeRow,
  feeRowTotal,
  feeLabelContainer,
  feeLabel,
  feeLabelTotal,
  feeAmount,
  feeAmountTotal,
  infoIcon,
  totalSection,
  totalRow,
  totalLabel,
  totalAmount,
} = STYLES();

// Additional Fees Accordion Item Component
const AdditionalFeesAccordionItem = ({
  renderFeeItem,
  additionalFeesBlock,
}: {
  renderFeeItem: (
    item: FeeItem,
    index: number,
    feeTooltipTitle: Field<string> | undefined,
    modalContent: React.ReactNode
  ) => React.JSX.Element;
  additionalFeesBlock: AdditionalFeesBlock;
}): React.JSX.Element => {
  const isEditing = useIsEditing();
  /*
   * REFS
   */
  const accordionItemButtonRef = useRef<HTMLButtonElement>(null);
  const accordionItemBaseRef = useRef<HTMLDivElement>(null);
  const accordionItemBodyRef = useRef<HTMLDivElement>(null);

  /*
   * State
   */
  const { isOpening, toggleOpen, isVisible, scrollToOpenPanel } = useAccordionItemContext(
    'additional-fees',
    accordionItemBodyRef
  );

  const {
    base,
    wrapper,
    buttonWrapper,
    iconWrapper,
    contentContainer,
    contentContainerInner,
    contentContainerInnerInner,
    headingText,
    descriptionText,
  } = ACCORDION_ITEM_STYLES({
    isOpening,
    isVisible,
  });

  /*
   * Convenience Methods
   */
  const toggleAccordion = useCallback(() => {
    toggleOpen();
  }, [toggleOpen]);

  /*
   * Lifecycle
   */
  const scrollCondition = scrollToOpenPanel && isOpening;

  useScrollElementIntoView(accordionItemButtonRef.current, {
    stickyHeaderId: 'header',
    condition: scrollCondition,
    animationElement: accordionItemBodyRef.current,
    scrollTargetId: `additional-fees-scroll-target`,
  });

  return (
    <div
      ref={accordionItemBaseRef}
      className={base()}
      data-component="authorable/shared/lists/accordionitem"
      {...editingHydrationProps(isEditing)}
      key="additional-fees"
      {...getTestProps(`accordion-item-additional-fees`)}
    >
      <div className={wrapper()}>
        <button
          id="additional-fees"
          aria-expanded={isOpening}
          aria-controls={`additional-fees-content`}
          className={buttonWrapper()}
          onClick={toggleAccordion}
          ref={accordionItemButtonRef}
          type="button"
          aria-label={additionalFeesBlock?.title?.value || 'Additional Fees'}
          {...getTestProps(`toggle-button`)}
        >
          <span>
            <PlainTextWrapper
              className={headingText()}
              field={additionalFeesBlock?.title}
              tag="h3"
              encode={false}
              {...getTestProps(`heading`)}
            />
            <RichTextWrapper
              className={descriptionText()}
              field={additionalFeesBlock?.description}
              tag="span"
              encode={false}
              {...getTestProps(`description`)}
            />
          </span>
          <span className={iconWrapper()} {...getTestProps(`icon`)}>
            <SvgIcon fill="none" icon="chevron-down" size="xs" viewBox="0 0 24 24" />
          </span>
        </button>
        <div
          id={`additional-fees-content`}
          aria-labelledby="additional-fees"
          className={contentContainer()}
          role="region"
          ref={accordionItemBodyRef}
          tabIndex={isOpening ? undefined : -1}
          aria-hidden={!isOpening}
        >
          <div className={contentContainerInner()}>
            <div className={contentContainerInnerInner()}>
              <div className={feeList()}>
                {additionalFeesBlock?.additionalFeesList &&
                Array.isArray(additionalFeesBlock.additionalFeesList)
                  ? additionalFeesBlock.additionalFeesList.map(
                      (item: Content.ConveyancingCalculator.GenericFeeSlab_Item, index: number) =>
                        renderFeeItem(
                          {
                            label: item.fields?.feeLabel?.value ?? '',
                            amount:
                              item.fields?.amount?.value ||
                              item.fields?.feeVariableText?.value ||
                              0,
                          },
                          index,
                          item.fields?.feeTooltipTitle,
                          <RichTextWrapper
                            field={item.fields?.feeTooltip}
                            tag="div"
                            encode={false}
                            {...getTestProps(`fee-tooltip`)}
                          />
                        )
                    )
                  : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ConveyancingStep2 = (props: ConveyancingStep2Props): React.JSX.Element => {
  const {
    guidanceBlock,
    additionalFeesBlock,
    step2FeesExclVat,
    step2MandatoryFees,
    totalObj,
  }: ConveyancingStep2Props = props;

  const renderFeeItem = (
    item: FeeItem,
    index: number,
    feeTooltipTitle: Field<string> | undefined,
    modalContent: React.ReactNode
  ) => {
    const isTotal = item?.isTotal;
    const RowComponent = isTotal ? feeRowTotal : feeRow;
    const LabelComponent = isTotal ? feeLabelTotal : feeLabel;
    const AmountComponent = isTotal ? feeAmountTotal : feeAmount;

    return (
      <div key={index} className={feeItem()}>
        <div className={RowComponent()}>
          {!isTotal ? (
            <ModalWrapper
              content={modalContent}
              title={feeTooltipTitle?.value ?? ''}
              trigger={
                <div className={feeLabelContainer()}>
                  <span className={LabelComponent()}>
                    {item?.label}
                    <span className={infoIcon()}>
                      <RemixIcon
                        className="!block"
                        name="information-line"
                        size="sm"
                        aria-hidden={true}
                      />
                    </span>
                  </span>
                </div>
              }
              id="additional-fees-modal"
              label="Additional Fees"
              openOnLoad={false}
            />
          ) : (
            <div className={feeLabelContainer()}>
              <span className={LabelComponent()}>{item?.label}</span>
            </div>
          )}
          <div className={AmountComponent()}>{formatCurrency(item?.amount ?? 0)}</div>
        </div>
      </div>
    );
  };

  const renderFeeSection = (feeSection: FeeSection) => (
    <div key={feeSection.title} className="flex flex-col gap-padding-tight">
      <h3 className={sectionTitle()}>{feeSection.title}</h3>
      <div className={feeList()}>
        {feeSection.items
          .filter(Boolean)
          .map((item: FeeItem, index: number) =>
            renderFeeItem(
              item,
              index,
              item?.feeTooltipTitle,
              <RichTextWrapper
                field={
                  item?.feeTooltip
                    ? typeof item.feeTooltip === 'string'
                      ? { value: item.feeTooltip }
                      : item.feeTooltip
                    : undefined
                }
                tag="div"
                encode={false}
                {...getTestProps(`fee-tooltip`)}
              />
            )
          )}
      </div>
    </div>
  );

  return (
    <section className={container()}>
      <div className={formGrid()}>
        <div className={form()}>
          <div className={fieldsColumn()}>
            <div className={formCard()}>
              {step2FeesExclVat && renderFeeSection(step2FeesExclVat)}
              {step2MandatoryFees && renderFeeSection(step2MandatoryFees)}

              {/* Total Section */}
              {((step2FeesExclVat?.items?.length ?? 0) > 0 ||
                (step2MandatoryFees?.items?.length ?? 0) > 0) && (
                <div className={totalSection()}>
                  <div className={totalRow()}>
                    <span className={totalLabel()}>{totalObj?.label}</span>
                    <span className={totalAmount()}>{formatCurrency(totalObj?.amount ?? 0)}</span>
                  </div>
                </div>
              )}

              {/* Additional Fees Accordion */}
              <AccordionContextProvider
                panels={[
                  {
                    uid: 'additional-fees',
                  } as ComponentRendering,
                ]}
                singleOpenPanel={false}
                scrollToOpenPanel={false}
                defaultOpenPanels={[]}
              >
                {additionalFeesBlock && (
                  <AdditionalFeesAccordionItem
                    renderFeeItem={(
                      item: FeeItem,
                      index: number,
                      feeTooltipTitle: Field<string> | undefined,
                      modalContent: React.ReactNode
                    ) => renderFeeItem(item, index, feeTooltipTitle, modalContent)}
                    additionalFeesBlock={additionalFeesBlock}
                  />
                )}
              </AccordionContextProvider>
            </div>
          </div>
        </div>
        {(guidanceBlock?.title?.value || guidanceBlock?.description?.value) && (
          <GuidanceSection guidanceBlock={guidanceBlock} />
        )}
      </div>
    </section>
  );
};

export default ConveyancingStep2;
